'use client';

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { safeSessionGet, safeSessionSet } from '@/lib/ui/safeStorage';

const STORAGE_KEY = 'promo-banner-dismissed';

// Flat, single colours — no gradients. Each banner colour is one solid fill so
// the bar reads as a deliberate block rather than a blend.
const BG_COLORS: Record<string, string> = {
  // Tomato (Pantone 18-1663 TPX) — the sale/urgency accent, default for promos.
  tomato: 'bg-[var(--sale)]',
  red: 'bg-red-700',
  blue: 'bg-blue-700',
  green: 'bg-emerald-700',
  purple: 'bg-purple-700',
  black: 'bg-gray-900',
};

interface BannerData {
  text: string;
  code: string;
  bgColor: string;
}

export default function PromoBanner() {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // safeSession*: a raw sessionStorage read throws with site data blocked,
    // and this effect runs on every page inside the app-wide ErrorBoundary.
    const dismissed = safeSessionGet(STORAGE_KEY);
    if (dismissed) return;

    fetch('/api/v2/commerce/active-banner')
      .then((res) => res.json())
      .then((data) => {
        if (data.banner) {
          setBanner(data.banner);
          setVisible(true);
        }
      })
      .catch(() => {});
  }, []);

  const dismiss = () => {
    setVisible(false);
    safeSessionSet(STORAGE_KEY, '1');
  };

  if (!visible || !banner) return null;

  const bgClass = BG_COLORS[banner.bgColor] || BG_COLORS.tomato;

  return (
    <div className={`relative ${bgClass} text-white text-center py-2 pl-3 pr-12 text-xs sm:text-sm`}>
      <p className="font-medium">
        {banner.text} |{' '}
        <span className="font-bold">Use Code: {banner.code}</span>
      </p>
      <button
        onClick={dismiss}
        aria-label="Dismiss promotional banner"
        className="absolute right-0 top-1/2 -translate-y-1/2 min-w-11 min-h-11 inline-flex items-center justify-center rounded hover:bg-white/20 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
